<template>
  <v-form autocomplete="off" @submit.prevent="$emit('save', article)">
    <v-card>
      <v-card-title>
        {{ $t('edit_article') }}
      </v-card-title>

      <v-card-text>
        <v-row class="pb-3" dense>
          <v-col cols="12" sm="9">
            <v-text-field
              v-model="article.title"
              :error-messages="errors['title']"
              :label="formMixin_getRequiredFieldLabel($t('title'))"
              name="title"
              autofocus
              @input="formMixin_clearErrors('title')"
            />
          </v-col>

          <v-col cols="12" sm="3">
            <v-text-field
              v-model.number="article.sequence"
              :error-messages="errors['sequence']"
              :label="$t('position')"
              type="number"
              min="1"
              @change="formMixin_clearErrors('sequence')"
            />
          </v-col>

          <v-col cols="12" sm="9">
            <v-radio-group
              v-model="article.type"
              :error-messages="errors['type']"
              :label="formMixin_getRequiredFieldLabel($t('type'))"
              class="mt-0"
              name="type"
              row
              @change="formMixin_clearErrors('type')"
            >
              <v-radio
                v-for="item in articleTypes"
                :key="item.label"
                :label="$t(item.label)"
                :value="item.label"
              ></v-radio>
            </v-radio-group>
          </v-col>

          <v-col cols="12" sm="3">
            <v-checkbox
              v-model="article.is_published"
              :error-messages="errors['is_published']"
              :label="$t('published')"
              class="float-none float-sm-right mt-0"
              @change="formMixin_clearErrors('is_published')"
            />
          </v-col>
        </v-row>

        <v-row class="pb-3" dense>
          <v-col v-if="article.images.length > 1" cols="12">
            <v-btn-toggle v-model="sortingMode" class="mb-1" mandatory>
              <v-btn :value="false" small>
                {{ $t('upload_image') }}
              </v-btn>

              <v-btn :value="true" small>
                {{ $t('change_sorting') }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12">
            <BaseFileDropZone
              :disabled="sortingMode"
              accept=".jpeg,.jpg,.bmp,.png,.gif"
              multiple
              @drop="onImageUpload($event, 'image', 'App\\Models\\Article', article.id)"
            >
              <draggable
                :value="article.images"
                :disabled="!sortingMode"
                animation="150"
                class="drop-zone d-flex flex-wrap"
                :class="imagesErrors.length ? 'error-outline' : ''"
                @end="onOrderChange"
              >
                <div v-if="!article.images.length" class="mx-auto">
                  <p class="my-8">{{ $t('multiple_file_drop_zone_message') }}</p>
                </div>
                <div v-for="image in article.images" :key="image.id" class="pa-2">
                  <v-img
                    :src="$backendUrl(image.image_path)"
                    :alt="image.original_name"
                    width="100px"
                    height="100px"
                    class="mx-auto"
                    contain
                  >
                    <v-icon v-if="sortingMode" x-large class="image-icon draggable"
                      >mdi-drag</v-icon
                    >
                  </v-img>
                  <div class="d-flex justify-space-between align-center image-name-box">
                    <p :class="{ draggable: sortingMode }" class="image-name text-truncate mb-0">
                      {{ image.original_name }}
                    </p>
                    <v-btn icon @click.stop="onImageDelete(image)"
                      ><v-icon>mdi-delete</v-icon></v-btn
                    >
                  </div>
                </div>
              </draggable>
            </BaseFileDropZone>

            <template v-if="imagesErrors.length">
              <div
                v-for="(error, errorIndex) in imagesErrors"
                :key="errorIndex"
                class="mt-2 v-messages theme--light error--text"
              >
                {{ error }}
              </div>
            </template>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12">
            <BaseTextEditor
              v-model="article.description"
              :error-messages="errors['description']"
              :label="formMixin_getRequiredFieldLabel($t('description'))"
              required
              name="description"
              @input="formMixin_clearErrors('description')"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <span v-if="$vuetify.breakpoint.smAndUp" class="text-subtitle-2 ml-3">
          * {{ $t('must_be_filled') }}
        </span>

        <v-spacer />

        <v-btn text color="secondary" @click="$emit('preview', article)">{{ $t('preview') }}</v-btn>

        <v-btn text color="primary" @click="$emit('cancel')">
          {{ $t('cancel') }}
        </v-btn>

        <v-btn :disabled="disabled" :loading="disabled" color="primary" text type="submit">
          {{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import draggable from 'vuedraggable';
import formMixin from '../mixins/form-mixin';
import BaseTextEditor from './base/BaseTextEditor';
import BaseFileDropZone from './base/BaseFileDropZone';

export default {
  name: 'ArticleForm',

  components: { BaseTextEditor, BaseFileDropZone, draggable },

  mixins: [formMixin],

  props: {
    article: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    imagesErrors: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      sortingMode: false,
      articleTypes: [{ label: 'news' }, { label: 'events' }],
    };
  },

  methods: {
    onImageUpload(image, type, modelType, modelId) {
      this.$emit('upload:image', image, type, modelType, modelId);
    },

    onImageDelete(image) {
      this.$emit('delete:image', image);
    },

    onOrderChange(event) {
      this.$emit('reorder:images', { oldIndex: event.oldIndex, newIndex: event.newIndex });
    },
  },
};
</script>

<style scoped>
.drop-zone {
  outline: 2px dashed darkgray;
  width: 100%;
}
.image-name-box {
  width: 120px;
}
</style>
